import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trash, DownloadSimple, FilePdf, SignOut, Warning, ShieldWarning, Pencil } from "@phosphor-icons/react";
import { toast } from "sonner";
import EditReportModal from "@/components/EditReportModal";

function fmtSeconds(sec) {
  if (sec == null) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${m}m`;
}

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [reports, setReports] = useState([]);
  const [objects, setObjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [totals, setTotals] = useState({ tasks: [], grand_total_seconds: 0, grand_count: 0 });
  const [filters, setFilters] = useState({ object_id: "", task_id: "", user_id: "", status: "", start_from: "", start_to: "", has_deviation: false });
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => v && (params[k] = v));
      const [r, o, t, u, tt] = await Promise.all([
        api.get("/reports", { params }),
        api.get("/objects"),
        api.get("/tasks"),
        api.get("/users"),
        api.get("/reports/totals-by-task", { params }),
      ]);
      setReports(r.data);
      setObjects(o.data);
      setTasks(t.data);
      setUsers(u.data);
      setTotals(tt.data || { tasks: [], grand_total_seconds: 0, grand_count: 0 });
    } catch (e) {
      // 401 hanteras av axios-interceptorn (auto-refresh + logout-event).
      // Övriga fel: visa toast så användaren vet, i stället för att blank:a sidan.
      if (e?.response?.status !== 401) {
        toast.error(e.response?.data?.detail || "Kunde inte ladda rapporter");
      }
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const deleteReport = async (id) => {
    if (!window.confirm("Ta bort rapport?")) return;
    try {
      await api.delete(`/reports/${id}`);
      toast.success("Borttagen");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fel");
    }
  };

  const forceClockOut = async (r) => {
    const who = r.user_name || "användaren";
    if (!window.confirm(`Tvinga ut ${who} ur "${r.object_name}"?\nRapporten markeras som tvångsstängd och sluttiden sätts till nu.`)) return;
    try {
      await api.post(`/reports/${r.report_id}/force-clock-out`);
      toast.success("Rapport tvångsstängd");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte tvinga utstämpling");
    }
  };

  const downloadReportPdf = async (r) => {
    try {
      const res = await api.get(`/reports/${r.report_id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const date = new Date(r.start_time).toISOString().slice(0, 10);
      const slug = (r.user_name || "anstalld")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "") || "anstalld";
      a.download = `tidsrapport-${date}-${slug}-${r.report_id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte skapa PDF");
    }
  };

  const total = useMemo(
    () => reports.reduce((s, r) => s + (r.duration_seconds || 0), 0),
    [reports]
  );

  // Group filtered reports by task_name (sorted alphabetically),
  // with newest-first inside each group.
  const groupedReports = useMemo(() => {
    const byTask = new Map();
    for (const r of reports) {
      const key = r.task_name || "—";
      if (!byTask.has(key)) byTask.set(key, []);
      byTask.get(key).push(r);
    }
    const groups = Array.from(byTask.entries()).map(([task_name, items]) => {
      const sorted = [...items].sort(
        (a, b) => new Date(b.start_time) - new Date(a.start_time)
      );
      const subtotal = sorted.reduce(
        (s, r) => s + (r.duration_seconds || 0),
        0
      );
      return { task_name, items: sorted, subtotal };
    });
    groups.sort((a, b) =>
      a.task_name.localeCompare(b.task_name, "sv", { sensitivity: "base" })
    );
    return groups;
  }, [reports]);

  const exportPdf = async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => v && (params[k] = v));
    try {
      const res = await api.get("/reports/export/pdf", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tidsrapport-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Kunde inte skapa PDF");
    }
  };

  const exportCsv = () => {
    const sep = ";";
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = [
      ["Datum", "Anställd", "Objekt", "Moment", "Start", "Slut", "Timmar", "Notering"].map(esc).join(sep),
    ];
    for (const g of groupedReports) {
      for (const r of g.items) {
        lines.push([
          new Date(r.start_time).toLocaleDateString("sv-SE", { timeZone: "Europe/Stockholm" }),
          r.user_name || "",
          r.object_name || "",
          r.task_name || "",
          new Date(r.start_time).toLocaleTimeString("sv-SE", { timeZone: "Europe/Stockholm" }),
          r.end_time ? new Date(r.end_time).toLocaleTimeString("sv-SE", { timeZone: "Europe/Stockholm" }) : "",
          ((r.duration_seconds || 0) / 3600).toFixed(2),
          (r.note || "").replace(/\n/g, " "),
        ].map(esc).join(sep));
      }
      // Per-group subtotal row
      lines.push([
        "", "", "", `Delsumma · ${g.task_name}`, "", "", (g.subtotal / 3600).toFixed(2), "",
      ].map(esc).join(sep));
    }
    // Grand total of the filtered listing
    lines.push([
      "", "", "", "", "", "Totalt (urval)", (total / 3600).toFixed(2), "",
    ].map(esc).join(sep));

    // Per-moment summary respecting the active filter
    if (totals.tasks && totals.tasks.length) {
      lines.push("");
      lines.push(["Sammanställning per moment — för valt urval"].map(esc).join(sep));
      lines.push(["Moment", "Antal rapporter", "Totalt timmar"].map(esc).join(sep));
      for (const t of totals.tasks) {
        lines.push([
          t.task_name || "",
          String(t.count || 0),
          ((t.total_seconds || 0) / 3600).toFixed(2),
        ].map(esc).join(sep));
      }
      lines.push([
        "Totalt",
        String(totals.grand_count || 0),
        ((totals.grand_total_seconds || 0) / 3600).toFixed(2),
      ].map(esc).join(sep));
    }

    const csv = lines.join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tidrapporter-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const setThisMonth = () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const iso = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    setFilters((f) => ({ ...f, start_from: iso(first), start_to: iso(last) }));
  };

  const clearDates = () => setFilters((f) => ({ ...f, start_from: "", start_to: "" }));

  return (
    <div className="space-y-6" data-testid="reports-page">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight">Rapporter</h1>
          <p className="text-text-muted mt-1">
            {reports.length} rapporter · totalt {fmtSeconds(total)}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={exportCsv} className="tk-btn-outline inline-flex items-center gap-2" data-testid="export-csv">
            <DownloadSimple size={18} /> Exportera CSV
          </button>
          <button onClick={exportPdf} className="tk-btn-outline inline-flex items-center gap-2" data-testid="export-pdf">
            <DownloadSimple size={18} /> Exportera PDF
          </button>
        </div>
      </header>

      <div className="tk-card p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {isAdmin && (
          <select
            value={filters.user_id}
            onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
            className="tk-input"
            data-testid="filter-user"
          >
            <option value="">Alla anställda</option>
            {users.map((u) => (
              <option key={u.user_id} value={u.user_id}>{u.name}</option>
            ))}
          </select>
        )}
        <select
          value={filters.object_id}
          onChange={(e) => setFilters({ ...filters, object_id: e.target.value })}
          className="tk-input"
          data-testid="filter-object"
        >
          <option value="">Alla objekt</option>
          {objects.map((o) => (
            <option key={o.object_id} value={o.object_id}>{o.name}</option>
          ))}
        </select>
        <select
          value={filters.task_id}
          onChange={(e) => setFilters({ ...filters, task_id: e.target.value })}
          className="tk-input"
          data-testid="filter-task"
        >
          <option value="">Alla moment</option>
          {tasks.map((t) => (
            <option key={t.task_id} value={t.task_id}>{t.name}</option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="tk-input"
          data-testid="filter-status"
        >
          <option value="">Alla statusar</option>
          <option value="active">Aktiv</option>
          <option value="completed">Slutförd</option>
        </select>
        <label
          className={`tk-input flex items-center gap-2 cursor-pointer select-none ${filters.has_deviation ? "border-brand text-brand font-medium" : "text-text-muted"}`}
          data-testid="filter-has-deviation-label"
          title="Visar rapporter där det finns antingen en avvikelse-text ELLER uppladdade foton/filmer på avvikelsen. Använd detta om du inte hittar en avvikelse du är säker på att du gjort — då syns den här oavsett vilket objekt/moment den ligger på, och även om du bara laddat upp foto utan text."
        >
          <input
            type="checkbox"
            checked={filters.has_deviation}
            onChange={(e) => setFilters({ ...filters, has_deviation: e.target.checked })}
            className="h-4 w-4 accent-brand"
            data-testid="filter-has-deviation"
          />
          Endast med avvikelse
        </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end">
          <label className="text-xs uppercase tracking-wider text-text-muted flex flex-col gap-1">
            Från datum
            <input
              type="date"
              value={filters.start_from}
              onChange={(e) => setFilters({ ...filters, start_from: e.target.value })}
              className="tk-input"
              data-testid="filter-start-from"
            />
          </label>
          <label className="text-xs uppercase tracking-wider text-text-muted flex flex-col gap-1">
            Till datum
            <input
              type="date"
              value={filters.start_to}
              onChange={(e) => setFilters({ ...filters, start_to: e.target.value })}
              className="tk-input"
              data-testid="filter-start-to"
            />
          </label>
          <button
            type="button"
            onClick={setThisMonth}
            className="tk-btn-outline"
            data-testid="filter-this-month"
          >
            Denna månad
          </button>
          <button
            type="button"
            onClick={clearDates}
            className="text-sm text-text-muted hover:text-text-strong underline-offset-4 hover:underline px-2 py-2"
            data-testid="filter-clear-dates"
          >
            Rensa datum
          </button>
        </div>
      </div>

      <div className="tk-card p-2 md:p-4">
        <div className="hidden md:grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-text-muted border-b border-app">
          <div className="col-span-2">Datum</div>
          {isAdmin && <div className="col-span-2">Anställd</div>}
          <div className={isAdmin ? "col-span-3" : "col-span-4"}>Objekt / Moment</div>
          <div className="col-span-2">Tid</div>
          <div className="col-span-2">Timmar</div>
          <div className="col-span-1 text-right">—</div>
        </div>
        {reports.length === 0 ? (
          <p className="text-center text-text-muted py-12 text-sm">Inga rapporter matchar filtren.</p>
        ) : (
          <>
            {groupedReports.map((g) => (
              <div key={g.task_name} data-testid={`report-group-${g.task_name}`}>
                <div
                  className="px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-text-muted bg-[var(--surface-muted,#f7f5f2)] border-b border-app"
                >
                  Moment · <span className="text-text-strong font-medium normal-case tracking-normal">{g.task_name}</span>
                </div>
                {g.items.map((r) => (
                  <div
                    key={r.report_id}
                    data-testid={`report-row-${r.report_id}`}
                    className="grid grid-cols-12 md:grid-cols-12 px-4 py-4 border-b border-app last:border-0 items-center text-sm"
                  >
                    <div className="col-span-6 md:col-span-2">
                      {new Date(r.start_time).toLocaleDateString("sv-SE", { timeZone: "Europe/Stockholm" })}
                    </div>
                    {isAdmin && (
                      <div className="col-span-6 md:col-span-2 truncate">{r.user_name}</div>
                    )}
                    <div className={`col-span-12 md:${isAdmin ? "col-span-3" : "col-span-4"} mt-1 md:mt-0`}>
                      <div className="font-heading">{r.object_name}</div>
                      <div className="text-xs text-text-muted">{r.task_name}</div>
                    </div>
                    <div className="col-span-6 md:col-span-2 text-text-muted tabular-nums">
                      {new Date(r.start_time).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Stockholm" })} –{" "}
                      {r.end_time
                        ? new Date(r.end_time).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Stockholm" })
                        : "—"}
                    </div>
                    <div className="col-span-4 md:col-span-2 tabular-nums">
                      {r.status === "active" ? (
                        <span className="tk-pill tk-pill-active">Aktiv</span>
                      ) : (
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span>{fmtSeconds(r.duration_seconds)}</span>
                          {r.auto_closed && (
                            <span
                              data-testid={`auto-closed-badge-${r.report_id}`}
                              title="Auto-utstämplad — passet översteg 12 timmar."
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide bg-amber-100 text-amber-900 border border-amber-300"
                            >
                              <Warning size={11} weight="fill" /> Auto-ut
                            </span>
                          )}
                          {r.force_closed && (
                            <span
                              data-testid={`force-closed-badge-${r.report_id}`}
                              title="Tvångsstängd av admin."
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide bg-rose-100 text-rose-900 border border-rose-300"
                            >
                              <ShieldWarning size={11} weight="fill" /> Tvingad
                            </span>
                          )}
                          {r.edited_at && (
                            <span
                              data-testid={`edited-badge-${r.report_id}`}
                              title={`Justerad ${new Date(r.edited_at).toLocaleString("sv-SE", { timeZone: "Europe/Stockholm" })}${r.edited_by_name ? ` av ${r.edited_by_name}` : ""}`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide bg-sky-100 text-sky-900 border border-sky-300"
                            >
                              <Pencil size={11} weight="fill" /> Justerad
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="col-span-2 md:col-span-1 text-right flex items-center justify-end gap-1">
                      {r.status === "completed" && (
                        <button
                          data-testid={`download-report-pdf-${r.report_id}`}
                          onClick={() => downloadReportPdf(r)}
                          className="text-text-muted hover:text-[var(--accent)]"
                          title="Ladda ner PDF"
                        >
                          <FilePdf size={18} />
                        </button>
                      )}
                      {isAdmin && r.status === "completed" && (
                        <button
                          data-testid={`edit-report-${r.report_id}`}
                          onClick={() => setEditing(r)}
                          className="text-text-muted hover:text-[var(--accent)]"
                          title="Justera tid"
                        >
                          <Pencil size={18} />
                        </button>
                      )}
                      {isAdmin && r.status === "active" && (
                        <button
                          data-testid={`force-clock-out-${r.report_id}`}
                          onClick={() => forceClockOut(r)}
                          className="text-text-muted hover:text-[var(--accent)]"
                          title="Tvinga utstämpla"
                        >
                          <SignOut size={18} />
                        </button>
                      )}
                      <button
                        data-testid={`delete-report-${r.report_id}`}
                        onClick={() => deleteReport(r.report_id)}
                        className="text-text-muted hover:text-[var(--status-danger)]"
                        title="Ta bort"
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>
                ))}
                <div
                  data-testid={`report-subtotal-${g.task_name}`}
                  className="grid grid-cols-12 px-4 py-3 border-b border-app bg-[var(--surface-muted,#f7f5f2)] items-center text-sm font-medium"
                >
                  <div className={`col-span-8 ${isAdmin ? "md:col-span-9" : "md:col-span-8"} text-text-strong`}>
                    Delsumma · {g.task_name}
                  </div>
                  <div className={`col-span-4 ${isAdmin ? "md:col-span-3" : "md:col-span-4"} tabular-nums text-right md:text-left`}>
                    {fmtSeconds(g.subtotal)}
                  </div>
                </div>
              </div>
            ))}
            <div
              data-testid="report-grand-total"
              className="grid grid-cols-12 px-4 py-4 items-center text-sm border-t-2 border-[var(--accent)]"
            >
              <div className={`col-span-8 ${isAdmin ? "md:col-span-9" : "md:col-span-8"} font-heading text-base text-[var(--accent)]`}>
                Totalt (urval)
              </div>
              <div className={`col-span-4 ${isAdmin ? "md:col-span-3" : "md:col-span-4"} tabular-nums text-right md:text-left font-heading text-base text-[var(--accent)]`}>
                {fmtSeconds(total)}
              </div>
            </div>
          </>
        )}
      </div>

      {totals.tasks && totals.tasks.length > 0 && (
        <div className="tk-card p-4 md:p-6 space-y-4" data-testid="totals-by-task-card">
          <div>
            <h2 className="font-heading text-2xl tracking-tight">Sammanställning per moment</h2>
            <p className="text-text-muted text-sm mt-1">
              Summor för rapporterna som visas ovan — uppdateras när du ändrar filter.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-text-muted border-b border-app">
                  <th className="py-2 pr-4 font-medium">Moment</th>
                  <th className="py-2 pr-4 font-medium text-right">Antal rapporter</th>
                  <th className="py-2 pr-4 font-medium text-right">Totalt</th>
                </tr>
              </thead>
              <tbody>
                {totals.tasks.map((t) => (
                  <tr
                    key={t.task_id || t.task_name}
                    data-testid={`totals-row-${t.task_name}`}
                    className="border-b border-app last:border-0"
                  >
                    <td className="py-2 pr-4">{t.task_name}</td>
                    <td className="py-2 pr-4 tabular-nums text-right">{t.count}</td>
                    <td className="py-2 pr-4 tabular-nums text-right">{fmtSeconds(t.total_seconds)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-[var(--accent)]" data-testid="totals-row-grand">
                  <td className="py-3 pr-4 font-heading text-base text-[var(--accent)]">Totalt</td>
                  <td className="py-3 pr-4 tabular-nums text-right font-heading text-base text-[var(--accent)]">
                    {totals.grand_count}
                  </td>
                  <td className="py-3 pr-4 tabular-nums text-right font-heading text-base text-[var(--accent)]">
                    {fmtSeconds(totals.grand_total_seconds)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <EditReportModal
          report={editing}
          objects={objects}
          tasks={tasks}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}
